import {
  findAndReplace,
  type ReplaceFunction,
} from 'mdast-util-find-and-replace'

type Tree = Parameters<typeof findAndReplace>[0]
type Replacement = ReturnType<ReplaceFunction>

// The mention node is a text node that the renderer maps to a `button`; it is
// cast here rather than added to mdast's node types globally.
type MentionNode = {
  type: 'mention'
  data: { hName: 'button'; hProperties: { dataMention: string } }
  children: [{ type: 'text'; value: string }]
}

const WORD_BEFORE = /[\w@-]/

function mention(name: string): Replacement {
  const node: MentionNode = {
    type: 'mention',
    data: { hName: 'button', hProperties: { dataMention: name } },
    children: [{ type: 'text', value: `@${name}` }],
  }
  return node as unknown as Replacement
}

/**
 * Turns `@[Display Name]` and bare `@identifier` into mention nodes, and
 * `#[name]` into the plain text `name`. Text nodes only, so code is untouched,
 * and links and the mentions already made are skipped.
 */
export function remarkThreadTokens() {
  return (tree: Tree) => {
    const bracketed = (_: string, name: string): Replacement => mention(name)
    const bare = (
      _: string,
      name: string,
      match: { index: number; input: string },
    ): Replacement => {
      // An @ inside a larger word (an email address) is not a mention.
      const before = match.input.charAt(match.index - 1)
      if (match.index > 0 && WORD_BEFORE.test(before)) return false
      return mention(name)
    }
    findAndReplace(
      tree,
      [
        [/@\[([^\]\n]+)\]/g, bracketed],
        [/@([\w-]+)/g, bare],
        [/#\[([^\]\n]+)\]/g, (_: string, name: string) => name],
      ],
      { ignore: ['link', 'linkReference', 'mention'] },
    )
  }
}

type Loose = {
  type: string
  value?: string
  label?: string | null
  identifier?: string
  children?: Loose[]
}

/**
 * GFM footnotes are not in the card's Markdown table, and their HTML (ids,
 * back-links, a hidden "Footnotes" heading) does not survive the renderer.
 * They become the plain text they were written as: `[^1]`, and `[^1]: …`.
 */
export function remarkPlainFootnotes() {
  return (tree: Tree) => {
    const walk = (node: Loose) => {
      if (!node.children) return
      node.children = node.children.flatMap((child): Loose[] => {
        const mark = `[^${child.label ?? child.identifier ?? ''}]`
        if (child.type === 'footnoteReference') {
          return [{ type: 'text', value: mark }]
        }
        if (child.type === 'footnoteDefinition') {
          walk(child)
          const blocks = child.children ?? []
          const prefix: Loose = { type: 'text', value: `${mark}: ` }
          const [first, ...rest] = blocks
          if (first?.type === 'paragraph') {
            return [
              { ...first, children: [prefix, ...(first.children ?? [])] },
              ...rest,
            ]
          }
          return [{ type: 'paragraph', children: [prefix] }, ...blocks]
        }
        walk(child)
        return [child]
      })
    }
    walk(tree as unknown as Loose)
  }
}
